import { AppSelect } from "./AppSelect";
import { useMemo, useState } from "react";
import { Check, ListPlus, MapPin, Search } from "lucide-react";
import { api } from "../lib/api";
import type { PlaceForecast } from "../lib/weather";

type Match = { query: string; candidates: PlaceForecast[]; selectedId?: string };

export function FavoriteImporter({ onImport }: { onImport: (places: PlaceForecast[]) => Promise<void> }) {
  const [value, setValue] = useState("");
  const [matches, setMatches] = useState<Match[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const selected = useMemo(() => matches.map((match) => match.candidates.find((place) => place.id === match.selectedId)).filter((place): place is PlaceForecast => Boolean(place)), [matches]);

  async function findMatches() {
    const queries = [...new Set(value.split(/\r?\n|;/).map((item) => item.trim()).filter(Boolean))].slice(0, 8);
    if (!queries.length) return;
    setBusy(true); setError(undefined);
    try {
      const results = await Promise.all(queries.map(async (query) => ({ query, candidates: (await api.geocode(query)).places.slice(0, 4) })));
      setMatches(results.map((match) => ({ ...match, selectedId: match.candidates[0]?.id })));
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Ricerca non disponibile"); }
    finally { setBusy(false); }
  }

  async function importSelected() {
    if (!selected.length) return;
    setBusy(true); setError(undefined);
    try { await onImport(selected); setValue(""); setMatches([]); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Importazione non riuscita"); }
    finally { setBusy(false); }
  }

  return <section className="favorite-importer">
    <div className="importer-title"><span className="eyebrow"><ListPlus /> Inserimento multiplo</span><p>Una località per riga. Prima di aggiungerle vedrai il riscontro restituito dalle API geografiche.</p></div>
    <textarea value={value} onChange={(event) => setValue(event.target.value)} rows={4} placeholder={"Marilleva\nLevico Terme\nOrtisei"} aria-label="Elenco località preferite" />
    <button className="match-button" onClick={() => void findMatches()} disabled={busy || !value.trim()}><Search />{busy ? "Cerco…" : "Trova corrispondenze"}</button>
    {error && <p className="import-error">{error}</p>}
    {!!matches.length && <div className="formal-matches">{matches.map((match, index) => <div className="formal-match" key={`${match.query}-${index}`}><span className="query-label">Hai scritto: <strong>{match.query}</strong></span>{match.candidates.length ? <label><MapPin /><span><small>Riscontro API</small><AppSelect label={`Località per ${match.query}`} value={match.selectedId ?? ""} onChange={value => setMatches(rows => rows.map((row, rowIndex) => rowIndex === index ? { ...row, selectedId: value } : row))} options={match.candidates.map(candidate => ({ value: candidate.id, label: candidate.matchLabel ?? candidate.name }))} /><em>{match.candidates.find((candidate) => candidate.id === match.selectedId)?.latitude.toFixed(4)}, {match.candidates.find((candidate) => candidate.id === match.selectedId)?.longitude.toFixed(4)}</em></span></label> : <p>Nessuna località italiana trovata.</p>}</div>)}</div>}
    {!!matches.length && <button className="import-button" onClick={() => void importSelected()} disabled={busy || !selected.length}><Check /> Aggiungi {selected.length} {selected.length === 1 ? "località" : "località"}</button>}
  </section>;
}

